import axios from "axios";
import { queryClient } from "./queryClient";

import { API_URL as API } from "./config";
export { API };
const TOKEN_KEY = "mailapp_token_v1";
const SESSION_KEY = "mailapp_session_v2";

axios.interceptors.request.use((config) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t && config.url?.startsWith(API)) config.headers.Authorization = "Bearer " + t;
  return config;
});

// Sessione scaduta/non valida sul server → torna al login
axios.interceptors.response.use(
  (r) => r,
  (err) => {
    const url = err?.config?.url || "";
    if (err?.response?.status === 401 && url.startsWith(API) && !url.includes("/auth/login") && !url.includes("/auth/register")) {
      clearSession();
      window.dispatchEvent(new Event("mailapp-logout"));
    }
    const st = err?.response?.status;
    if (url.startsWith(API) && !url.includes("/errori") && !url.includes("/emails/ping") && (!st || st >= 500 || st === 424) && localStorage.getItem(TOKEN_KEY)) {
      segnalaErrore({
        messaggio: `${err?.config?.method?.toUpperCase() || ""} ${url.replace(API, "")} → ${st || "rete"}: ${err?.response?.data?.detail || err?.message || "errore"}`,
        url: window.location.pathname,
        livello: "error",
      });
    }
    return Promise.reject(err);
  }
);

// Dati di accesso ricordati (email + password) per precompilare il login
const LOGIN_KEY = "mailapp_login_v1";
export function saveLoginData(email, password) {
  localStorage.setItem(LOGIN_KEY, btoa(unescape(encodeURIComponent(JSON.stringify({ email, password })))));
}
export function loadLoginData() {
  try {
    const raw = localStorage.getItem(LOGIN_KEY);
    return raw ? JSON.parse(decodeURIComponent(escape(atob(raw)))) : null;
  } catch (e) {
    return null;
  }
}
export function clearLoginData() {
  localStorage.removeItem(LOGIN_KEY);
}

export function saveSession(token, user) {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(SESSION_KEY, JSON.stringify(user));
}
export function clearSession() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(SESSION_KEY);
  navigator.serviceWorker?.controller?.postMessage({ tipo: "svuota-email" }); // cache locale delle email del service worker
}
export function loadSession() {
  try {
    if (!localStorage.getItem(TOKEN_KEY)) return null;
    const raw = localStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

// Autenticazione
const authOk = (r) => {
  saveSession(r.data.token, r.data.user);
  return r.data.user;
};
export const register = (nome, email, password) =>
  axios.post(`${API}/auth/register`, { nome, email, password }).then(authOk);
export const login = (email, password) => axios.post(`${API}/auth/login`, { email, password }).then(authOk);
export const me = () => axios.get(`${API}/auth/me`).then((r) => r.data);
export const deleteMe = () => axios.delete(`${API}/auth/me`);
export const forgotPassword = (email) => axios.post(`${API}/auth/forgot-password`, { email }).then((r) => r.data);
export const resetPassword = (email, code, password) =>
  axios.post(`${API}/auth/reset-password`, { email, code, password }).then((r) => r.data);

export const getAccounts = () => axios.get(`${API}/accounts`).then((r) => r.data);
export const addAccount = (payload) => axios.post(`${API}/accounts`, payload).then((r) => r.data);
// Account social (Pagina Facebook / Messenger): token della Pagina verificato dal server
export const addSocialAccount = (payload) => axios.post(`${API}/accounts/social`, payload).then((r) => r.data);
export const deleteAccount = (id) => axios.delete(`${API}/accounts/${id}`);
export const updateAccount = (id, payload) => axios.put(`${API}/accounts/${id}`, payload).then((r) => r.data);
export const getEmails = (accountId = "tutti", offset = 0, limit = 20, refresh = false, cursore = null) =>
  axios
    .get(`${API}/emails`, { params: { account_id: accountId, offset: cursore ? 0 : offset, limit, refresh: refresh ? 1 : 0, ...(cursore ? { before_ts: cursore.before_ts, before_id: cursore.before_id } : {}) } })
    .then((r) => r.data);
// Controllo nuove email sul server IMAP (la lista risponde subito dal DB, questo va dopo)
export const getErrori = (params = {}) => axios.get(`${API}/errori`, { params }).then((r) => r.data);
export const soluzioneErrore = (id) => axios.post(`${API}/errori/${id}/soluzione`, null, { timeout: 60000 }).then((r) => r.data);
export const setRelay = (accountId, payload) => axios.put(`${API}/accounts/${accountId}/relay`, payload).then((r) => r.data);
// Memo del calendario
export const getMemo = () => axios.get(`${API}/memo`).then((r) => r.data);
export const addMemo = (payload) => axios.post(`${API}/memo`, payload).then((r) => r.data);
export const setMemoFatto = (id, fatto) => axios.put(`${API}/memo/${id}`, { fatto }).then((r) => r.data);
export const deleteMemo = (id) => axios.delete(`${API}/memo/${id}`).then((r) => r.data);
export const svuotaErrori = () => axios.delete(`${API}/errori`).then((r) => r.data);
export const segnalaErrore = (payload) => axios.post(`${API}/errori`, payload, { timeout: 8000 }).then((r) => r.data).catch(() => {});
export const refreshEmails = (accountId = "tutti", background = false) =>
  axios.post(`${API}/emails/refresh`, null, { params: { account_id: accountId, background: background ? 1 : 0 } }).then((r) => r.data);
export const getSyncStatus = () => axios.get(`${API}/sync/status`).then((r) => r.data);
export const startSync = () => axios.post(`${API}/sync/start`).then((r) => r.data);
// Dettaglio email con cache react-query: stessa email riaperta entro 5 minuti = nessuna richiesta; richieste
// contemporanee per lo stesso id vengono unite. Invalidare con invalidaDettaglio(id) dopo modifiche lato server.
export const getEmailDetail = (id) =>
  queryClient.fetchQuery({
    queryKey: ["email-detail", id],
    queryFn: () => axios.get(`${API}/emails/detail`, { params: { id } }).then((r) => {
      if (r.data?.provvisorio) setTimeout(() => invalidaDettaglio(id), 0); // corpo non disponibile: nessuna cache, riprova alla prossima apertura
      return r.data;
    }),
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
  });
export const invalidaDettaglio = (id) => queryClient.removeQueries({ queryKey: id ? ["email-detail", id] : ["email-detail"] });
// Solo gli id delle email che contengono il testo ovunque (corpo compreso) in tutta la posta: le righe vengono dall'indice locale
export const searchIds = (q) => axios.get(`${API}/emails/search/ids`, { params: { q } }).then((r) => r.data);
export const getEmailsDetails = (ids) => axios.get(`${API}/emails/details`, { params: { ids: ids.join(",") } }).then((r) => r.data);
// Preferenze (tema, filtri) e preferiti sul server: identici su web e PWA con lo stesso login
export const getPrefs = () => axios.get(`${API}/me/prefs`).then((r) => r.data);
export const putPrefs = (prefs) => axios.put(`${API}/me/prefs`, prefs).then((r) => r.data);
export const starEmail = (id, preferito) => axios.post(`${API}/emails/star`, { id, preferito }).then((r) => r.data);
export const moveInbox = (id, in_arrivo) => axios.post(`${API}/emails/move-inbox`, { id, in_arrivo }).then((r) => r.data);
export const setStato = (id, stato) => axios.post(`${API}/emails/stato`, { id, stato }).then((r) => r.data);
export const regolaMittente = (email, regola) => axios.post(`${API}/emails/regola-mittente`, { email, regola }).then((r) => r.data);
export const saveDraft = (d) => axios.put(`${API}/drafts`, d).then((r) => r.data);
export const deleteDraft = (id) => axios.delete(`${API}/drafts/${encodeURIComponent(id)}`).then((r) => r.data);
export const purgeEliminate = (ids) => axios.post(`${API}/emails/svuota-eliminate`, { ids: ids || [] }).then((r) => r.data);
// Diagnostica: server, database, utente, versione (per confrontare web e PWA)
export const bootstrap = () => axios.get(`${API}/bootstrap`).then((r) => r.data);
// Indice compatto di tutte le intestazioni (ricerca istantanea nel browser); since_ts = solo le nuove
export const getIndice = (sinceTs = 0, sinceMod = 0) => axios.get(`${API}/emails/indice`, { params: { since_ts: sinceTs, since_mod: sinceMod }, timeout: 120000 }).then((r) => r.data);
export const ping = () => axios.get(`${API}/emails/ping`, { timeout: 8000 }).then((r) => r.data);
export const diagnostica = () => axios.get(`${API}/health`).then((r) => r.data);
export const cercaContatti = (q) => axios.get(`${API}/contacts`, { params: { q } }).then((r) => r.data);
// Ricerca completa: DB (tutta la posta importata) + server IMAP (email vecchie non ancora scaricate)
// archivio: 0 = solo recenti (ultimi 90 giorni, pagine da 20), 1 = solo archivio (pagine da 10), 2 = tutto
export const searchEmails = (q, server = 1, opts = {}) =>
  axios.get(`${API}/emails/search`, { params: { q, server, pec: opts.pec ? 1 : 0, pec_tipo: opts.pecTipo || "", offset: opts.offset || 0, limit: 20, archivio: opts.archivio || 0 } }).then((r) => r.data);
export const backupInfo = () => axios.get(`${API}/backup/info`).then((r) => r.data);
// Coda "In uscita" (invii falliti con riprova automatica) e stato connessione caselle
export const getOutbox = () => axios.get(`${API}/outbox`).then((r) => r.data);
export const retryOutbox = (id) => axios.post(`${API}/outbox/${id}/retry`).then((r) => r.data);
export const deleteOutbox = (id) => axios.delete(`${API}/outbox/${id}`).then((r) => r.data);
export const getAccountsStato = () => axios.get(`${API}/accounts/stato`).then((r) => r.data);
export const checkAccountStato = (id) => axios.post(`${API}/accounts/${id}/stato`).then((r) => r.data);
// Amministrazione utenti (solo role=admin)
export const adminUsers = () => axios.get(`${API}/admin/users`).then((r) => r.data);
export const adminSetPassword = (id, password) => axios.post(`${API}/admin/users/${id}/password`, { password }).then((r) => r.data);
export const adminSetRole = (id, role) => axios.post(`${API}/admin/users/${id}/role`, { role }).then((r) => r.data);
export const adminDeleteUser = (id) => axios.delete(`${API}/admin/users/${id}`).then((r) => r.data);
// SSO PAQ: il cookie access_token di .areapaq.it parte da solo (stesso dominio in produzione)
export const ssoSession = () => axios.get(`${API}/sso/session`, { withCredentials: true }).then((r) => r.data);
export const backupUrl = () => `${API}/backup/download?token=${encodeURIComponent(localStorage.getItem(TOKEN_KEY) || "")}`;
export const sendEmail = (payload) => axios.post(`${API}/emails/send`, payload).then((r) => r.data);

// Upload allegato a blocchi (600 KB): il corpo della richiesta di invio resta piccolo, nessun limite dei proxy
function base64ToBytes(b64) {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}
export const uploadAttachment = async (att, onProgress) => {
  const bytes = base64ToBytes(att.dati || "");
  const start = await axios.post(`${API}/uploads/start`, { nome: att.nome, tipo: att.tipo || "application/octet-stream", size: bytes.length }).then((r) => r.data);
  const size = start.chunk_size || 600 * 1024;
  let index = 0;
  for (let off = 0; off < bytes.length; off += size, index++) {
    await axios.post(`${API}/uploads/${start.upload_id}/chunk`, bytes.slice(off, off + size), { params: { index }, headers: { "Content-Type": "application/octet-stream" } });
    if (onProgress) onProgress(Math.min(1, (off + size) / bytes.length));
  }
  await axios.post(`${API}/uploads/${start.upload_id}/finish`);
  return { nome: att.nome, tipo: att.tipo || "application/octet-stream", upload_id: start.upload_id };
};
// Prepara gli allegati per l'invio: quelli con dati (file scelti) vengono caricati a blocchi
export const preparaAllegati = async (allegati, onProgress) => {
  const out = [];
  for (let i = 0; i < (allegati || []).length; i++) {
    const a = allegati[i];
    if (a.dati) out.push(await uploadAttachment(a, (p) => onProgress && onProgress(i, p)));
    else out.push({ nome: a.nome, tipo: a.tipo || "", origine_id: a.origine_id || "", origine_part: a.origine_part ?? -1 });
  }
  return out;
};
export const markRead = (id, letto = true) => axios.post(`${API}/emails/read`, { id, letto });
export const deleteRemote = (id) => axios.post(`${API}/emails/delete`, { id });
export const aiAction = (mode, testo, oggetto = "", contesto = "") =>
  axios.post(`${API}/ai`, { mode, testo, oggetto, contesto }).then((r) => r.data.result);
// AI in diretta: il testo arriva a pezzi; onChunk(testoAccumulato) a ogni pezzo. Ritorna il testo completo.
export async function aiActionStream(mode, testo, oggetto = "", contesto = "", onChunk) {
  const r = await fetch(`${API}/ai/stream`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: "Bearer " + (localStorage.getItem(TOKEN_KEY) || "") },
    body: JSON.stringify({ mode, testo, oggetto, contesto }),
  });
  if (!r.ok) {
    let det = "L'AI non è al momento disponibile";
    try { det = (await r.json()).detail || det; } catch (e) { console.debug("AI stream: risposta d'errore non JSON", r.status); }
    throw new Error(det);
  }
  const reader = r.body.getReader();
  const dec = new TextDecoder();
  let acc = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    acc += dec.decode(value, { stream: true });
    if (acc.includes("[ERRORE_AI]")) throw new Error(acc.split("[ERRORE_AI]")[1].trim());
    onChunk?.(acc);
  }
  return acc.trim();
}
export const aiRisposte = (testo, oggetto = "", mittente = "") =>
  axios.post(`${API}/ai/risposte`, { testo, oggetto, mittente }).then((r) => r.data.risposte);
export const attachmentUrl = (id, part, download = false) =>
  `${API}/emails/attachment?id=${encodeURIComponent(id)}&part=${part}${download ? "&download=1" : ""}&token=${encodeURIComponent(localStorage.getItem(TOKEN_KEY) || "")}`;
export const attachmentThumbUrl = (id, part) =>
  `${API}/emails/attachment/thumb?id=${encodeURIComponent(id)}&part=${part}&token=${encodeURIComponent(localStorage.getItem(TOKEN_KEY) || "")}`;
export const saveFirma = (accountId, firmaTesto, firmaImg, firmaFont = "Arial", firmaSize = 14, firmaLink = "", firmaImgPos = "sotto", firmaImgLarg = 130) =>
  axios.put(`${API}/accounts/${accountId}/firma`, {
    firma_testo: firmaTesto,
    firma_img: firmaImg,
    firma_font: firmaFont,
    firma_size: firmaSize,
    firma_link: firmaLink,
    firma_img_pos: firmaImgPos,
    firma_img_larg: firmaImgLarg,
  });
